import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, GraduationCap, Check, Loader2, Lightbulb, Cpu, Wrench, Calculator } from 'lucide-react';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants
const fadeInLeft = { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 } };
const fadeInRight = { initial: { opacity: 0, x: 20 }, animate: { opacity: 1, x: 0 } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

const stemSubjects = [
  { id: 'science', name: 'Science', icon: Lightbulb, color: 'bg-yellow-500', description: 'Explore the world through experiments' },
  { id: 'technology', name: 'Technology', icon: Cpu, color: 'bg-blue-500', description: 'Learn coding and digital skills' },
  { id: 'engineering', name: 'Engineering', icon: Wrench, color: 'bg-orange-500', description: 'Build and create amazing projects' },
  { id: 'maths', name: 'Maths', icon: Calculator, color: 'bg-green-500', description: 'Problem-solving and logical thinking' },
];

const ageGroups = [
  { id: '4-6', label: '4-6 years', description: 'Little Explorers' },
  { id: '7-10', label: '7-10 years', description: 'Junior Scientists' },
  { id: '11-14', label: '11-14 years', description: 'Young Innovators' },
];

const StemRegistrationPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(null);
  const [formData, setFormData] = useState({
    parent_name: '',
    child_name: '',
    mobile_phone: '',
    email: '',
    additional_info: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedAgeGroup) {
      toast.error(t('stemRegistration.selectAgeGroupError'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/registrations/stem`, {
        ...formData,
        age_group: selectedAgeGroup
      });
      
      toast.success(t('stemRegistration.successTitle'), {
        description: t('stemRegistration.successDescription')
      });
      
      // Reset form
      setFormData({ parent_name: '', child_name: '', mobile_phone: '', email: '', additional_info: '' });
      setSelectedAgeGroup(null);
      
    } catch (error) {
      toast.error(t('stemRegistration.errorTitle'), {
        description: error.response?.data?.detail || t('stemRegistration.errorDescription')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream">
      <Header />
      
      <main className="pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Back Button */}
          <motion.button
            initial={fadeInLeft.initial}
            animate={fadeInLeft.animate}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors mb-8 font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('stemRegistration.backToHome')}
          </motion.button>

          {/* Page Header */}
          <motion.div
            initial={fadeInUp.initial}
            animate={fadeInUp.animate}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
              <GraduationCap className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('stemRegistration.title')}
            </h1>
            <p className="font-body text-lg text-gray-600 max-w-2xl mx-auto">
              {t('stemRegistration.subtitle')}
            </p>
            <div className="mt-4 inline-block px-4 py-2 rounded-full bg-green-100 text-green-700 font-heading font-bold text-sm">
              {t('stemRegistration.freeBadge')}
            </div>
          </motion.div>

          {/* STEM Subjects Overview */}
          <motion.div
            initial={fadeInUp.initial}
            animate={fadeInUp.animate}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <h2 className="font-heading text-2xl font-bold text-gray-900 text-center mb-6">
              {t('stemRegistration.whatLearn')}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stemSubjects.map((subject) => (
                <div
                  key={subject.id}
                  className="bg-white rounded-2xl p-6 text-center shadow-md hover:shadow-lg transition-shadow"
                >
                  <div className={cn("w-14 h-14 mx-auto rounded-2xl flex items-center justify-center text-white mb-4", subject.color)}>
                    <subject.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-heading font-bold text-gray-900 mb-1">{t(`stemRegistration.subjects.${subject.id}.name`)}</h3>
                  <p className="font-body text-sm text-gray-500">{t(`stemRegistration.subjects.${subject.id}.description`)}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Age Group Selection */}
            <motion.div
              initial={fadeInLeft.initial}
              animate={fadeInLeft.animate}
              transition={{ delay: 0.2 }}
              className="space-y-6"
            >
              <h2 className="font-heading text-2xl font-bold text-gray-900">
                {t('stemRegistration.selectAgeGroup')}
              </h2>
              
              <div className="space-y-4">
                {ageGroups.map((group) => (
                  <div
                    key={group.id}
                    data-testid={`age-group-${group.id}`}
                    onClick={() => setSelectedAgeGroup(group.id)}
                    className={cn(
                      "relative p-6 rounded-2xl border-2 cursor-pointer transition-all",
                      selectedAgeGroup === group.id
                        ? "border-green-500 bg-green-50 shadow-lg"
                        : "border-gray-200 bg-white hover:border-green-300 hover:shadow-md"
                    )}
                  >
                    {selectedAgeGroup === group.id && (
                      <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    
                    <h3 className="font-heading font-bold text-xl text-gray-900">{t(`stemRegistration.ageGroups.${group.id}.label`)}</h3>
                    <p className="font-body text-gray-500">{t(`stemRegistration.ageGroups.${group.id}.description`)}</p>
                  </div>
                ))}
              </div>

              {/* Benefits */}
              <div className="bg-green-50 rounded-2xl p-6 mt-6">
                <h3 className="font-heading font-bold text-lg text-gray-900 mb-4">{t('stemRegistration.whyChoose')}</h3>
                <ul className="space-y-3">
                  {t('stemRegistration.benefits').map((benefit, idx) => (
                    <li key={idx} className="flex items-center gap-3 font-body text-gray-700">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>

            {/* Registration Form */}
            <motion.div
              initial={fadeInRight.initial}
              animate={fadeInRight.animate}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6">
                  {t('stemRegistration.registerChild')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Parent Name */}
                  <div className="space-y-2">
                    <Label htmlFor="parent_name" className="font-heading font-bold text-gray-700">
                      {t('stemRegistration.parentName')}
                    </Label>
                    <Input
                      id="parent_name"
                      name="parent_name"
                      data-testid="stem-parent-name-input"
                      value={formData.parent_name}
                      onChange={handleInputChange}
                      placeholder={t('stemRegistration.parentNamePlaceholder')}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                    />
                  </div>

                  {/* Child Name */}
                  <div className="space-y-2">
                    <Label htmlFor="child_name" className="font-heading font-bold text-gray-700">
                      {t('stemRegistration.childName')}
                    </Label>
                    <Input
                      id="child_name"
                      name="child_name"
                      data-testid="stem-child-name-input"
                      value={formData.child_name}
                      onChange={handleInputChange}
                      placeholder={t('stemRegistration.childNamePlaceholder')}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="mobile_phone" className="font-heading font-bold text-gray-700">
                      {t('stemRegistration.mobilePhone')}
                    </Label>
                    <Input
                      id="mobile_phone"
                      name="mobile_phone"
                      data-testid="stem-phone-input"
                      value={formData.mobile_phone}
                      onChange={handleInputChange}
                      placeholder="695521384"
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-heading font-bold text-gray-700">
                      {t('stemRegistration.emailAddress')}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="stem-email-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('stemRegistration.emailPlaceholder')}
                      required
                      className="h-12 rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                    />
                  </div>

                  {/* Additional Info */}
                  <div className="space-y-2">
                    <Label htmlFor="additional_info" className="font-heading font-bold text-gray-700">
                      {t('stemRegistration.additionalInfo')}
                    </Label>
                    <Textarea
                      id="additional_info"
                      name="additional_info"
                      data-testid="stem-info-input"
                      value={formData.additional_info}
                      onChange={handleInputChange}
                      placeholder={t('stemRegistration.additionalInfoPlaceholder')}
                      rows={3}
                      className="rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="stem-submit-btn"
                    disabled={loading || !selectedAgeGroup}
                    className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-full font-heading font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t('stemRegistration.registering')}
                      </>
                    ) : (
                      <>
                        <GraduationCap className="w-5 h-5 mr-2" />
                        {t('stemRegistration.registerForStem')}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StemRegistrationPage;
